import { useMemo } from 'react'

import { SearchFormData } from '@components/SearchForm'
import locationUtils from '@lib/location'
import { useCarrierLoader } from '@loaders/carrier'
import { useLocationName } from '@loaders/locations'
import useLocationNames from '@queries/locations'
import { useSettings } from '@queries/settings'
import { ParamsStore } from '@stores/params'

export type SearchParams = Omit<SearchFormData, 'departureLocation' | 'arrivalLocation' | 'stopOverStations'> &
  Pick<
    ParamsStore,
    | 'departureLocation'
    | 'arrivalLocation'
    | 'locale'
    | 'currency'
    | 'stopOverStations'
    | 'marketingCarrierCode'
    | 'fareClass'
  >

const { getLocationType } = locationUtils

const useSearchFormInitial = (params: SearchParams): SearchFormData => {
  const { data: departureLocation } = useLocationName({ locale: params.locale, location: params.departureLocation })
  const { data: arrivalLocation } = useLocationName({ locale: params.locale, location: params.arrivalLocation })
  const { data: stopOverStations } = useLocationNames(params.stopOverStations)

  const [{ openReturn }] = useSettings()
  const isOpenReturnTripEnabled = openReturn.enabled
  const { data: marketingCarrier } = useCarrierLoader({
    marketingCarrierCode: isOpenReturnTripEnabled ? params.marketingCarrierCode : null,
    params: { locale: params.locale, currency: params.currency },
  })

  return useMemo(
    () => ({
      ...params,
      departureLocation,
      arrivalLocation,
      stopOverStations: stopOverStations.map(data => ({ ...data, type: getLocationType(data.code), children: [] })),
      isOpenReturn:
        isOpenReturnTripEnabled && marketingCarrier?.fareClasses.some(fareClass => fareClass.code === params.fareClass),
      fareClass: params.fareClass || null,
    }),
    [arrivalLocation, departureLocation, params, stopOverStations, marketingCarrier, isOpenReturnTripEnabled],
  )
}

export default useSearchFormInitial
